/**
 * Prolense Backoffice + Ticketing + SQA — frontend contract module.
 * Cites: .claude/design/backoffice-ticketing-pathkey-sqa.md (Sub-workstreams A and C).
 *
 * Everything here mirrors the LIVE backend contract (policylens-backend/src/services/
 * supportTicket.service.ts, sqa.service.ts, controllers/*.routes.ts) — enums are copied
 * verbatim from `CATEGORIES` / `PRIORITIES` / `STATUSES` / `SQA_TOOL_NAMES`; nothing is
 * invented. The server is the source of truth for scoping (Commandment III): a reporter is
 * only ever returned their own tenant's own tickets, support staff see every tenant. Nothing
 * in this module filters for security — `filterTicketsForDisplay` is a pure UI convenience
 * over rows the server already scoped.
 *
 * Envelope: every route answers `{ success: true, data }` or
 * `{ success: false, error: { code, message, details } }` (utils/apiResponse.ts).
 */

import { ApiError, apiErrorFromResponse, toApiError } from './apiError';

export const SUPPORT_CATEGORIES = ['billing', 'usage', 'feature', 'bug', 'engagement_blocker', 'clarification', 'training'];
export const SUPPORT_PRIORITIES = ['urgent', 'high', 'medium', 'low'];
export const SUPPORT_STATUSES = ['new', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
/** Server input bounds (supportTicket.service.ts) — enforced again server-side; shown here for UX. */
export const DESCRIPTION_MAX_CHARS = 20000;
export const MESSAGE_BODY_MAX_CHARS = 10000;
export const ATTACHMENT_FILENAME_MAX_CHARS = 255;
export const MAX_ATTACHMENTS_PER_TICKET = 20;
/** Mirror of the backend ATTACHMENT_ALLOWED_TYPES (MIME type -> allowed extensions). No html/svg/js/zip. */
export const ATTACHMENT_ALLOWED_TYPES = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
    'application/pdf': ['.pdf'],
    'text/plain': ['.txt', '.log'],
    'text/csv': ['.csv'],
    'application/json': ['.json'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};
/** Value for <input accept> — the file picker only offers allowed extensions. */
export const ATTACHMENT_ACCEPT = Object.values(ATTACHMENT_ALLOWED_TYPES).flat().join(',');

/**
 * UX pre-check only (the server is the control: MIME + extension + magic bytes).
 * @returns {string|null} an error message, or null when the file may be sent.
 */
export function validateAttachmentFile(file, existingCount = 0) {
    if (!file) return 'Choose a file.';
    if (existingCount >= MAX_ATTACHMENTS_PER_TICKET) return `A ticket may carry at most ${MAX_ATTACHMENTS_PER_TICKET} attachments.`;
    if (!file.name || file.name.length > ATTACHMENT_FILENAME_MAX_CHARS) return `File name must be 1-${ATTACHMENT_FILENAME_MAX_CHARS} characters.`;
    const exts = ATTACHMENT_ALLOWED_TYPES[file.type];
    const lower = file.name.toLowerCase();
    if (!exts || !exts.some((e) => lower.endsWith(e))) {
        return `This file type is not allowed. Allowed: ${Object.values(ATTACHMENT_ALLOWED_TYPES).flat().join(', ')}.`;
    }
    return null;
}

export const SQA_TOOL_NAMES = ['sqa_playwright', 'sqa_k6', 'sqa_postman'];

/** Backend enum value -> display label ("engagement_blocker" -> "Engagement blocker"). Derived
 *  mechanically from the value so a label can never disagree with the contract. */
export function humanizeEnum(value) {
    if (!value) return '-';
    const s = String(value).replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
}

export const PRIORITY_COLOR = { urgent: 'red', high: 'orange', medium: 'gold', low: 'default' };
export const STATUS_COLOR = {
    new: 'blue',
    in_progress: 'processing',
    waiting_customer: 'warning',
    resolved: 'success',
    closed: 'default',
};

// Single shared formatters (Commandment XII) — see utils/formatDdMmYyyy.js.
export { formatDdMmYyyy, formatDdMmYyyyTime } from './formatDdMmYyyy';

/** Ticket/user/tenant ids are UUIDs — show a short form, keep the full value for tooltips. */
export function shortId(id) {
    return id ? String(id).slice(0, 8) : '-';
}

const isOpenStatus = (status) => status !== 'resolved' && status !== 'closed';

/**
 * SLA display state derived ONLY from real ticket fields (`slaBreached`,
 * `slaResolutionDueAt`, `status`). The backend has no first-response timestamp, so first
 * response is never claimed met or missed here — only its due date is shown by the caller.
 * @returns {'breached' | 'overdue' | 'open' | 'done'}
 */
export function resolutionSlaState(ticket, now = new Date()) {
    if (!ticket) return 'open';
    if (!isOpenStatus(ticket.status)) return ticket.slaBreached ? 'breached' : 'done';
    if (ticket.slaBreached) return 'breached';
    const due = new Date(ticket.slaResolutionDueAt).getTime();
    if (!Number.isNaN(due) && now.getTime() > due) return 'overdue';
    return 'open';
}

/** UI-only narrowing of rows the server already scoped (never a security control). */
export function filterTicketsForDisplay(tickets, { priority, category, search, projectLabels } = {}) {
    const q = (search || '').trim().toLowerCase();
    return (tickets || []).filter((t) => {
        if (priority && t.priority !== priority) return false;
        if (category && t.category !== category) return false;
        if (q) {
            const label = (projectLabels && t.projectId && projectLabels[t.projectId]) || '';
            const hay = [t.title, t.description, t.id, t.tenantId, t.tenantName, t.projectId, t.reporterUserId, t.reporterName, t.reporterEmail, label]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    });
}

export { ApiError };

/**
 * Builds the typed client. `getToken` resolves an MSAL access token; `baseUrl` is
 * `apiConfig.baseUrl`. The request layer never sends tenantId/projectId from the client —
 * the server derives the actor's tenant from the token (Commandment III).
 */
export function createSupportApi(getToken, baseUrl, fetchImpl) {
    const doFetch = fetchImpl || ((...args) => fetch(...args));

    async function request(path, { method = 'GET', body, formData, full = false } = {}) {
        let token;
        try {
            token = await getToken();
        } catch (err) {
            throw toApiError(err);
        }
        const headers = { Authorization: `Bearer ${token}` };
        let payload;
        if (formData) {
            payload = formData;
        } else if (body !== undefined) {
            headers['Content-Type'] = 'application/json';
            payload = JSON.stringify(body);
        }
        let res;
        try {
            res = await doFetch(`${baseUrl}/api/v1${path}`, { method, headers, body: payload });
        } catch (err) {
            throw toApiError(err);
        }
        const contentType = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
        const json = contentType.includes('json') ? await res.json().catch(() => null) : null;
        if (!res.ok || !json || json.success === false) {
            throw apiErrorFromResponse(res, json);
        }
        return full ? { data: json.data, meta: json.meta || {} } : json.data;
    }

    const qs = (params) => {
        const p = Object.entries(params || {}).filter(([, v]) => v);
        return p.length ? `?${p.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}` : '';
    };

    return {
        me: () => request('/me'),
        /** Paginated, severity-sorted (server). Staff rows carry tenantName / reporterName / reporterEmail. */
        listTickets: (filters) =>
            request(`/support/tickets${qs(filters)}`, { full: true }).then(({ data, meta }) => ({
                tickets: data.tickets,
                total: meta.total ?? data.tickets.length,
                page: meta.page ?? 1,
                pageSize: meta.pageSize ?? data.tickets.length,
            })),
        /** Assignee picker source — support staff only (route-gated). */
        listSupportStaff: () => request('/admin/support/staff').then((d) => d.staff),
        /** Short-lived signed URL; staff reads of another tenant's ticket are audited server-side. */
        getAttachmentDownload: (ticketId, attachmentId) =>
            request(`/support/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}/download`).then((d) => d.download),
        createTicket: (input) => request('/support/tickets', { method: 'POST', body: input }).then((d) => d.ticket),
        patchTicket: (id, patch) =>
            request(`/support/tickets/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }).then((d) => d.ticket),
        listMessages: (id) => request(`/support/tickets/${encodeURIComponent(id)}/messages`).then((d) => d.messages),
        addMessage: (id, input) =>
            request(`/support/tickets/${encodeURIComponent(id)}/messages`, { method: 'POST', body: input }).then((d) => d.message),
        listAttachments: (id) => request(`/support/tickets/${encodeURIComponent(id)}/attachments`).then((d) => d.attachments),
        uploadAttachment: (id, file) => {
            const formData = new FormData();
            formData.append('file', file);
            return request(`/support/tickets/${encodeURIComponent(id)}/attachments`, { method: 'POST', formData }).then((d) => d.attachment);
        },
        listRoutingRules: () => request('/admin/support/routing-rules').then((d) => d.rules),
        upsertRoutingRule: (input) => request('/admin/support/routing-rules', { method: 'POST', body: input }).then((d) => d.rule),

        /** Lead Advisor / platform_admin only (route-level). The secret is never returned. */
        getPathkeyConfig: () => request('/admin/pathkey-config').then((d) => d.config),
        listPathkeyEvents: (params) =>
            request(`/admin/pathkey-events${qs(params)}`, { full: true }).then(({ data, meta }) => ({
                events: data.events,
                total: meta.total ?? data.events.length,
                page: meta.page ?? 1,
                pageSize: meta.pageSize ?? data.events.length,
            })),
        savePathkeyConfig: (input) => request('/admin/pathkey-config', { method: 'POST', body: input }).then((d) => d.config),

        // Software QA Toolkit (Sub-workstream C)
        listSqaTemplates: (projectType) => request(`/sqa/templates${qs({ projectType })}`).then((d) => d.templates),
        listSqaResults: (projectId) => request(`/projects/${encodeURIComponent(projectId)}/sqa/results`).then((d) => d.results),
        uploadSqaResult: (projectId, toolName, file) => {
            const formData = new FormData();
            formData.append('toolName', toolName);
            formData.append('file', file);
            return request(`/projects/${encodeURIComponent(projectId)}/sqa/results`, { method: 'POST', formData }).then((d) => d.result);
        },
        reviewSqaResult: (projectId, resultId) =>
            request(`/projects/${encodeURIComponent(projectId)}/sqa/results/${encodeURIComponent(resultId)}/review`, {
                method: 'POST',
            }).then((d) => d.result),
    };
}
